import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductBackButton from './ProductBackButton';

const ProductBackContext = createContext({
  setBackHandler: () => {},
  clearBackHandler: () => {},
});

/** Paths where the Chat/Apps/Cases footer is the nav — no floating back. */
const FOOTER_TAB_PATHS = new Set(['/chat', '/apps', '/cases']);

/**
 * Marketing / auth / legal / editorial — no product back chrome.
 * Keep this allowlist tight so nested product tools always get a back control.
 */
export function isNonProductPath(pathname) {
  if (!pathname || pathname === '/') return true;
  if (matches(pathname)) return true;
  // React Router matches `/about/` to `/about`; the exact Set is literal, so
  // also test the path with trailing slashes stripped. The raw check above
  // stays first so prefix entries such as '/features/' still match.
  const stripped = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return stripped !== pathname && matches(stripped);
}

function matches(pathname) {
  if (FOOTER_TAB_PATHS.has(pathname)) return true;

  const exact = new Set([
    '/about', // IVORI company home, rendered by app.js
    '/login',
    '/reset-password',
    '/privacy',
    '/terms-of-use',
    '/terms',
    '/safeguarding',
    '/cookies',
    '/legal',
    '/support',
    '/pricing',
    '/upgrade',
    '/plans',
    '/subscription/success',
    '/admin',
    '/admin/content-manager',
    '/admin/plan-manager',
    '/all-apps',
    '/insights',
  ]);
  if (exact.has(pathname)) return true;

  const prefixes = [
    '/legal/',
    '/login/callback',
    '/auth/',
    '/features/',
    '/insights/',
    '/method/',
    '/case-studies/',
    '/verify',
    '/tmua',
    '/lnat',
    '/ucat',
    '/tara',
    '/esat',
    '/oxbridge',
    '/medicalschool',
    '/degreeapprenticeship',
    '/degree-apprenticeships',
    '/specialty/',
  ];
  return prefixes.some((p) => pathname === p || pathname.startsWith(p));
}

function defaultBackTarget(pathname) {
  if (pathname.startsWith('/case-study')) return '/cases';
  if (pathname === '/profile' || pathname === '/settings') return '/apps';
  return '/apps';
}

function isLeftRailProductPath(pathname) {
  if (!pathname) return false;
  if (pathname === '/caius-explore' || pathname.startsWith('/caius-explore/')) return true;
  if (pathname === '/library' || pathname.startsWith('/library/')) return true;
  if (/\/apps-store\/[^/]+\/caius-explore\/?$/.test(pathname)) return true;
  if (/\/apps-store\/[^/]+\/library\/?$/.test(pathname)) return true;
  return false;
}

/**
 * Mount outside nested product routes (when footer is hidden).
 * Pages can override the action via useProductBackHandler for one-level back.
 */
export function ProductBackProvider({ children, enabled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [handler, setHandler] = useState(null);

  const setBackHandler = useCallback((fn) => {
    setHandler(() => fn || null);
  }, []);

  const clearBackHandler = useCallback(() => {
    setHandler(null);
  }, []);

  const value = useMemo(
    () => ({ setBackHandler, clearBackHandler }),
    [setBackHandler, clearBackHandler]
  );

  // Drop stale page handlers on route change so default parent applies.
  useEffect(() => {
    setHandler(null);
  }, [location.pathname]);

  const show =
    enabled &&
    !isNonProductPath(location.pathname);

  const railClass = isLeftRailProductPath(location.pathname)
    ? 'product-back-btn--clear-rail'
    : '';

  // Let pages reserve corner space so the fixed back never covers scroll content.
  useEffect(() => {
    if (!show) {
      document.body.classList.remove('product-back-visible', 'product-back-clear-rail');
      return undefined;
    }
    document.body.classList.add('product-back-visible');
    if (railClass) document.body.classList.add('product-back-clear-rail');
    else document.body.classList.remove('product-back-clear-rail');
    return () => {
      document.body.classList.remove('product-back-visible', 'product-back-clear-rail');
    };
  }, [show, railClass]);

  const onClick = () => {
    if (typeof handler === 'function') {
      handler();
      return;
    }
    navigate(defaultBackTarget(location.pathname));
    window.scrollTo(0, 0);
  };

  const ariaLabel = location.pathname.startsWith('/case-study')
    ? 'Back to cases'
    : location.pathname === '/profile' || location.pathname === '/settings'
      ? 'Back to apps'
      : 'Back to apps';

  return (
    <ProductBackContext.Provider value={value}>
      {children}
      {show && (
        <ProductBackButton
          onClick={onClick}
          ariaLabel={ariaLabel}
          className={railClass}
        />
      )}
    </ProductBackContext.Provider>
  );
}

/**
 * Register a one-level-back handler while this component is mounted.
 * Example: in-quiz → clear quiz and return to the tests list.
 */
export function useProductBackHandler(handler) {
  const { setBackHandler, clearBackHandler } = useContext(ProductBackContext);

  useEffect(() => {
    if (!handler) {
      clearBackHandler();
      return undefined;
    }
    setBackHandler(handler);
    return () => clearBackHandler();
  }, [handler, setBackHandler, clearBackHandler]);
}

export default ProductBackProvider;
